import { useEffect, useState } from 'react';
import { useTimeout } from 'usehooks-ts';

import useCreditSubscription from '@/hooks/api/useCreditSubscription';

const POLL_INTERVAL_MS = 500;
const POLL_TIMEOUT_MS = 10_000;

const useSubscriptionPolling = ({
  organizationSlug,
  onChanged,
}: {
  organizationSlug: string;
  onChanged: () => void;
}) => {
  const [isPolling, setIsPolling] = useState(false);
  const [originalUpdatedAt, setOriginalUpdatedAt] = useState<number | null>(null);

  const { data } = useCreditSubscription(
    { slug: organizationSlug },
    {
      enabled: isPolling,
      refetchInterval: (query) => {
        const current = query.state.data?.updatedAt;
        if (current != null && current !== originalUpdatedAt) return false;
        return POLL_INTERVAL_MS;
      },
      refetchIntervalInBackground: true,
    },
  );

  useEffect(() => {
    if (!isPolling || originalUpdatedAt == null) return;
    if (data?.updatedAt != null && data.updatedAt !== originalUpdatedAt) {
      onChanged();
    }
  }, [data?.updatedAt, isPolling, originalUpdatedAt, onChanged]);

  useTimeout(onChanged, isPolling ? POLL_TIMEOUT_MS : null);

  const startPolling = (baseline: number | null) => {
    if (baseline == null) {
      onChanged();
      return;
    }
    setOriginalUpdatedAt(baseline);
    setIsPolling(true);
  };

  return { isPolling, startPolling };
};

export default useSubscriptionPolling;
